import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { MotionConfig } from "framer-motion";
import { App } from "./App";
import { AuthProvider } from "@/contexts/AuthContext";
import { EmpresaProvider } from "@/contexts/EmpresaContext";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";
import { AppToaster } from "@/components/ui/Toast";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { initErrorReporter } from "@/lib/errorReporter";
import { FeatureFlagsPanel } from "@/components/debug/FeatureFlagsPanel";
import "@/styles/index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // Frescura garantizada por invalidación explícita (mutations, WebSocket,
      // useFacturaRefresh); el caché de 30s/5min evita refetch al navegar.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

const container = document.getElementById("root");
if (!container) throw new Error("No se encontró #root");

initErrorReporter();

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {import.meta.env.DEV ? (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      ) : null}
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <MotionConfig reducedMotion="user">
          <AuthProvider>
            <EmpresaProvider>
              <FeatureFlagsProvider>
                <ErrorBoundary>
                  <App />
                  <FeatureFlagsPanel />
                </ErrorBoundary>
                <AppToaster />
              </FeatureFlagsProvider>
            </EmpresaProvider>
          </AuthProvider>
        </MotionConfig>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
